import { useLayoutEffect, useRef, useState } from 'react';

const DURATION = 180;
const EASING = 'cubic-bezier(0.2, 0, 0, 1)';

function cancelAnimation(ref) {
  if (!ref.current) return;
  ref.current.onfinish = null;
  ref.current.cancel();
  ref.current = null;
}

export function useDisclosureTransition(open, { ref: suppliedRef, expand = false, onEntered } = {}) {
  const ownRef = useRef(null);
  const ref = suppliedRef || ownRef;
  const animationRef = useRef(null);
  const enteredRef = useRef(onEntered);
  const [mounted, setMounted] = useState(open);
  const present = open || mounted;
  enteredRef.current = onEntered;

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const previous = animationRef.current;
    // Read the current frame before cancelling, so rapid taps reverse without jumping.
    const fromHeight = previous || !open ? element.getBoundingClientRect().height : 0;
    const fromOpacity = previous || !open ? getComputedStyle(element).opacity : 0;
    const fromTransform = previous ? getComputedStyle(element).transform : 'translateY(-4px)';
    cancelAnimation(animationRef);
    delete element.dataset.transitioning;
    if (open) setMounted(true);

    const finish = () => {
      delete element.dataset.transitioning;
      if (open) enteredRef.current?.(element);
      else setMounted(false);
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !element.animate) {
      finish();
      return;
    }

    const frames = expand
      ? [
        { height: `${fromHeight}px`, opacity: fromOpacity },
        { height: open ? `${element.getBoundingClientRect().height}px` : '0px', opacity: open ? 1 : 0 },
      ]
      : [
        { opacity: fromOpacity, transform: open || previous ? fromTransform : 'translateY(0)' },
        { opacity: open ? 1 : 0, transform: open ? 'translateY(0)' : 'translateY(-4px)' },
      ];
    element.dataset.transitioning = 'true';
    const animation = element.animate(frames, { duration: DURATION, easing: EASING, fill: 'both' });
    animationRef.current = animation;
    animation.onfinish = () => {
      // Keep the zero-height final frame until React removes the closed panel.
      if (open) cancelAnimation(animationRef);
      finish();
    };
  }, [open, expand, ref]);

  useLayoutEffect(() => {
    if (!present) cancelAnimation(animationRef);
  }, [present]);
  useLayoutEffect(() => () => cancelAnimation(animationRef), []);

  return { ref, present };
}
